/**
 * Instance Operations Tab (DRR-165)
 *
 * Shows live operational context for a running workflow instance:
 * - Progress bar (completed/total tasks)
 * - Current state with available transitions (fire event controls)
 * - State machine visualization (InstanceStateDiagram)
 * - State transition history (append-only timeline)
 * - Spawned tasks (clickable, linked to task detail)
 *
 * Extracted from InstanceDetailPage — replaces the standalone page
 * with a DRY tab in the universal detail popup.
 *
 * FERP Principles:
 * - Dumb Frontend: all data from backend GraphQL
 * - Repository pattern: mutations via generated hooks
 * - Life-critical: state transitions are append-only audit trail
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Group,
  Text,
  Badge,
  Alert,
  Progress,
  Paper,
  Divider,
  Timeline,
  Button,
  Select,
  Tooltip,
  ThemeIcon,
  Loader,
  ScrollArea,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconGitBranch,
  IconHistory,
  IconShield,
  IconStack2,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  useGetInstanceHistoryQuery,
  useGetWorkflowQuery,
  useGetTasksByWorkflowInstanceQuery,
  useListActorsQuery,
  GqlInstanceStatus,
  GqlTaskStatus,
  type Instance,
  type Task,
} from '@/generated/graphql';
import { useTenantId } from '@/auth';
import { useDevActorId } from '@/hooks/useDevActorId';
import type { TabRendererProps } from '@ferp/workflow-forms';

// ── Inline mutations ─────────────────────────────────────────────────────────

const FIRE_EVENT_MUTATION = gql`
  mutation FireEvent($instanceId: String!, $eventName: String!) {
    fireEvent(instanceId: $instanceId, eventName: $eventName) {
      id status currentState previousState totalTasks completedTasks version
      availableTransitions { event toState displayName guard }
    }
  }
`;

const COMPLETE_TASK_MUTATION = gql`
  mutation CompleteTask($workflowInstanceId: String!, $input: GqlCompleteTaskInput!) {
    completeTask(workflowInstanceId: $workflowInstanceId, input: $input) {
      id status
    }
  }
`;

const TRANSITION_TASK_MUTATION = gql`
  mutation TransitionTask($workflowInstanceId: String!, $taskId: String!, $input: GqlTransitionTaskInput!) {
    transitionTask(workflowInstanceId: $workflowInstanceId, taskId: $taskId, input: $input) {
      id status
    }
  }
`;

interface AvailableTransition {
  event: string;
  toState: string;
  displayName: string;
  guard: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  RUNNING: 'blue', COMPLETED: 'green', FAILED: 'red', PAUSED: 'orange',
  PENDING: 'yellow', CANCELLED: 'gray', TIMED_OUT: 'red',
};

const TASK_STATUS_COLORS: Record<string, string> = {
  PENDING: 'yellow', READY: 'cyan', ASSIGNED: 'blue', IN_PROGRESS: 'blue',
  COMPLETED: 'green', FAILED: 'red', CANCELLED: 'gray', PAUSED: 'orange',
};

function formatState(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'COMPLETED': case GqlInstanceStatus.COMPLETED:
      return <IconCircleCheck size={12} color="green" />;
    case 'FAILED': case GqlInstanceStatus.FAILED:
      return <IconCircleX size={12} color="red" />;
    case 'RUNNING': case GqlInstanceStatus.RUNNING:
      return <IconPlayerPlay size={12} color="blue" />;
    default:
      return <IconClock size={12} color="gray" />;
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export function InstanceOperationsTab({ entity, primitiveType }: TabRendererProps) {
  const tenantId = useTenantId() ?? '';
  const navigate = useNavigate();
  useDevActorId(); // auth context — actor assignment uses dropdown
  const instance = entity as Record<string, unknown>;
  const instanceId = instance.id as string;
  const workflowDefinitionId = instance.workflowDefinitionId as string | undefined;

  // Local override state — updated from fireEvent mutation response
  const [localOverride, setLocalOverride] = useState<Record<string, unknown> | null>(null);
  const effective = localOverride ?? instance;

  const currentState = effective.currentState as string;
  const previousState = effective.previousState as string | undefined;
  const status = effective.status as string;
  const totalTasks = (effective.totalTasks as number) ?? 0;
  const completedTasks = (effective.completedTasks as number) ?? 0;
  const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const availableTransitions = (effective.availableTransitions as AvailableTransition[] | undefined) ?? [];
  const isRunning = status === GqlInstanceStatus.RUNNING || status === 'RUNNING';

  // Reset override when parent entity changes (e.g., user clicks a different instance)
  useEffect(() => { setLocalOverride(null); }, [instanceId]);

  // Actor list for assignment dropdown (DRR-179)
  const { data: actorsData } = useListActorsQuery({ variables: { limit: 200 }, fetchPolicy: 'cache-first' });
  const actorOptions = useMemo(() => {
    const items = actorsData?.listActors?.items ?? [];
    return items
      .filter((a) => a.actorType === 'HUMAN')
      .map((a) => ({ value: a.id, label: a.name || a.id }));
  }, [actorsData]);

  // Fire event mutation
  const [fireEvent, { loading: fireEventLoading }] = useMutation<{ fireEvent: { id: string; status: string; currentState: string; previousState?: string; totalTasks: number; completedTasks: number; version: number; availableTransitions: AvailableTransition[] } }>(FIRE_EVENT_MUTATION);
  const [fireEventError, setFireEventError] = useState<string | null>(null);

  const handleFireEvent = useCallback(async (eventName: string) => {
    setFireEventError(null);
    try {
      const result = await fireEvent({
        variables: { instanceId, eventName },
        refetchQueries: ['GetInstance', 'GetInstanceHistory', 'GetTasksByWorkflowInstance'],
      });
      // Update local state from mutation response so UI updates immediately
      const updated = result.data?.fireEvent;
      if (updated) {
        setLocalOverride({
          ...instance,
          ...updated,
        });
      }
    } catch (err) {
      setFireEventError(err instanceof Error ? err.message : 'Transition failed');
    }
  }, [instanceId, fireEvent, instance]);

  // Task mutations — transitionTask handles assign and complete
  const [transitionTaskMutation] = useMutation(TRANSITION_TASK_MUTATION);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [assigningTaskId, setAssigningTaskId] = useState<string | null>(null);

  // Task mutations use workflowDefinitionId for DDB fallback compatibility.
  // Valkey-first path resolves by task_id alone; DDB fallback needs SK prefix match.
  // Task spawner writes DDB with workflow_instance_id = instance_id ?? workflow_ref,
  // and Valkey index by workflow_ref (definition ID). Use definition ID for consistency.
  const handleCompleteTask = useCallback(async (taskId: string) => {
    setCompletingTaskId(taskId);
    try {
      await transitionTaskMutation({
        variables: {
          workflowInstanceId: workflowDefinitionId ?? instanceId ?? '',
          taskId,
          input: { newStatus: 'COMPLETED', trigger: 'user_action' },
        },
        refetchQueries: ['GetTasksByWorkflowInstance'],
      });
    } catch (err) {
      console.error('Task completion failed:', err);
    } finally {
      setCompletingTaskId(null);
    }
  }, [transitionTaskMutation, workflowDefinitionId, instanceId]);

  const handleAssignTask = useCallback(async (taskId: string, assigneeId: string) => {
    setAssigningTaskId(taskId);
    try {
      await transitionTaskMutation({
        variables: {
          workflowInstanceId: workflowDefinitionId ?? instanceId ?? '',
          taskId,
          input: { newStatus: 'ASSIGNED', trigger: 'user_assigned', assignedTo: assigneeId },
        },
        refetchQueries: ['GetTasksByWorkflowInstance'],
      });
    } catch (err) {
      console.error('Task assignment failed:', err);
    } finally {
      setAssigningTaskId(null);
    }
  }, [transitionTaskMutation, workflowDefinitionId, instanceId]);

  // Workflow definition for state machine viz
  const { data: wfData } = useGetWorkflowQuery({
    variables: { workflowId: workflowDefinitionId ?? '' },
    skip: !workflowDefinitionId,
    fetchPolicy: 'cache-first',
  });

  // State transition history
  const { data: historyData } = useGetInstanceHistoryQuery({
    variables: { tenantId, workflowDefinitionId: workflowDefinitionId ?? '', instanceId },
    skip: !workflowDefinitionId,
  });
  const history: Instance[] = historyData?.instanceApiGetInstanceHistory ?? [];

  // Spawned tasks — Valkey index is keyed by workflow definition ID (wf-4-0-033),
  // NOT instance snowflake. Task spawner writes idx:~{tenant}:workflow:{wf_def_id}.
  const { data: tasksData } = useGetTasksByWorkflowInstanceQuery({
    variables: { workflowInstanceId: workflowDefinitionId ?? '' },
    skip: !workflowDefinitionId,
    pollInterval: 10_000,
  });
  const tasks: Task[] = tasksData?.taskApiGetTasksByWorkflowInstance ?? [];

  // Policy refs for current state
  const currentStatePolicyRefs: string[] = React.useMemo(() => {
    if (!wfData?.getWorkflow?.states || !currentState) return [];
    const state = wfData.getWorkflow.states.find(
      (s: { name: string }) => s.name === currentState,
    );
    if (!state) return [];
    const refs = (state as Record<string, unknown>).policyRefs;
    return Array.isArray(refs) ? (refs as string[]) : [];
  }, [wfData, currentState]);

  // Resolved workflow name/description from the already-fetched definition
  const workflowName = wfData?.getWorkflow?.name;
  const workflowDesc = wfData?.getWorkflow?.description;

  return (
    <Stack gap="md">
      {/* Workflow Context */}
      {workflowName && (
        <Paper p="sm" withBorder style={{ backgroundColor: 'var(--mantine-color-dark-7)' }}>
          <Text size="md" fw={600}>{workflowName}</Text>
          {workflowDesc && <Text size="xs" c="dimmed" mt={2}>{workflowDesc}</Text>}
        </Paper>
      )}

      {/* Progress */}
      {totalTasks > 0 && (
        <Paper p="sm" withBorder>
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={500}>Progress</Text>
            <Text size="sm" c="dimmed">{completedTasks}/{totalTasks} tasks</Text>
          </Group>
          <Progress value={progressPercent} size="sm" color="blue" />
        </Paper>
      )}

      {/* Current State + Next Steps */}
      <Group align="flex-start" gap="md" wrap="nowrap">
        <Paper withBorder p="sm" style={{ flex: 1 }}>
          <Stack gap="sm">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconGitBranch size={14} />
              </ThemeIcon>
              <Text size="sm" fw={600}>Current State</Text>
            </Group>
            <Paper p="xs" withBorder style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
              <Text size="md" fw={600} c="blue">{formatState(currentState)}</Text>
              {previousState && (
                <Text size="xs" c="dimmed" mt={2}>Previous: {formatState(previousState)}</Text>
              )}
            </Paper>

            {/* Policy Gates */}
            {currentStatePolicyRefs.length > 0 && (
              <>
                <Divider label="Policy Gates" labelPosition="left" />
                <Stack gap={4}>
                  {currentStatePolicyRefs.map(ref => (
                    <Paper key={ref} p="xs" withBorder style={{ backgroundColor: 'var(--mantine-color-green-0)' }}>
                      <Group gap="xs">
                        <ThemeIcon size="xs" variant="light" color="green"><IconShield size={10} /></ThemeIcon>
                        <Text size="xs" fw={500}>{ref}</Text>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}

            {/* Available Transitions */}
            {isRunning && availableTransitions.length > 0 && (
              <>
                <Divider label="Available Actions" labelPosition="left" />
                {fireEventError && (
                  <Alert color="orange" title="Transition Blocked"><Text size="xs">{fireEventError}</Text></Alert>
                )}
                <Stack gap={6}>
                  {availableTransitions.map(t => {
                    const hasGuard = !!t.guard;
                    return (
                      <Paper key={t.event} p="xs" withBorder style={{ cursor: hasGuard ? 'not-allowed' : 'pointer' }}>
                        <Group justify="space-between" wrap="nowrap">
                          <Stack gap={2} style={{ flex: 1 }}>
                            <Group gap="xs">
                              <Text size="sm" fw={600}>{t.displayName}</Text>
                              {hasGuard && <Badge size="xs" variant="outline" color="orange">Requires: {t.guard?.replace(/_/g, ' ')}</Badge>}
                            </Group>
                            <Text size="xs" c="dimmed">
                              Advances workflow from {formatState(currentState)} to {formatState(t.toState)}
                            </Text>
                          </Stack>
                          <Button
                            size="xs" variant={hasGuard ? 'outline' : 'filled'}
                            color={hasGuard ? 'gray' : 'teal'}
                            loading={fireEventLoading}
                            disabled={hasGuard}
                            onClick={() => handleFireEvent(t.event)}
                          >
                            {hasGuard ? 'Blocked' : 'Execute'}
                          </Button>
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              </>
            )}
          </Stack>
        </Paper>

        {/* History */}
        <Paper withBorder p="sm" style={{ flex: 1 }}>
          <Stack gap="sm">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="gray"><IconHistory size={14} /></ThemeIcon>
              <Text size="sm" fw={600}>State Transition History</Text>
              <Badge size="xs" variant="outline">Append-Only</Badge>
            </Group>
            <ScrollArea style={{ maxHeight: 300 }}>
              {history.length === 0 ? (
                <Text size="xs" c="dimmed">No history yet.</Text>
              ) : (
                <Timeline active={history.length - 1} bulletSize={18} lineWidth={2}>
                  {history.map((entry) => (
                    <Timeline.Item
                      key={entry.id}
                      bullet={<StatusIcon status={entry.status} />}
                      title={
                        <Group gap="xs">
                          <Text size="xs" fw={500}>{formatState(entry.currentState)}</Text>
                          <Badge size="xs" color={STATUS_COLORS[entry.status] ?? 'gray'} variant="light">{entry.status}</Badge>
                          <Badge size="xs" variant="outline">v{entry.version}</Badge>
                        </Group>
                      }
                    >
                      <Text size="xs" c="dimmed">{formatTimestamp(entry.updatedAt)}</Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              )}
            </ScrollArea>
          </Stack>
        </Paper>
      </Group>

      {/* Spawned Tasks */}
      {tasks.length > 0 && (
        <Paper withBorder p="sm">
          <Stack gap="sm">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="violet"><IconStack2 size={14} /></ThemeIcon>
              <Text size="sm" fw={600}>Spawned Tasks</Text>
              <Badge size="xs" variant="outline">{tasks.filter((t: { status: string }) => t.status === GqlTaskStatus.COMPLETED).length}/{tasks.length}</Badge>
            </Group>
            <Stack gap="xs">
              {tasks.map((task) => {
                const isCompleted = task.status === GqlTaskStatus.COMPLETED;
                const isAssigned = task.status === GqlTaskStatus.ASSIGNED;
                const isInProgress = task.status === GqlTaskStatus.IN_PROGRESS || isAssigned;
                const isPending = task.status === GqlTaskStatus.PENDING || task.status === GqlTaskStatus.READY;
                const isCompleting = completingTaskId === task.id;
                const isAssigning = assigningTaskId === task.id;
                const assignedActorObj = task.assignedActor as { id?: string; displayName?: string } | null;
                const hasForm = (((task as Record<string, unknown>).formComponents as string[] | undefined)?.length ?? 0) > 0;
                return (
                  <Paper
                    key={task.id}
                    p="xs"
                    withBorder
                    style={{
                      ...(isCompleted ? { opacity: 0.6 } : {}),
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/ferp-core/tasks/${task.id}`)}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>{task.name}</Text>
                        {task.description && <Text size="xs" c="dimmed" truncate>{task.description}</Text>}
                        {assignedActorObj && (
                          <Text size="xs" c="blue">Assigned: {assignedActorObj.displayName ?? assignedActorObj.id ?? ''}</Text>
                        )}
                      </Stack>
                      <Group gap="xs" wrap="nowrap">
                        <Badge size="xs" color={TASK_STATUS_COLORS[task.status] ?? 'gray'} variant="light">
                          {task.status.replace(/_/g, ' ')}
                        </Badge>
                        {isPending && (
                          <Select
                            size="xs"
                            placeholder="Assign to..."
                            data={actorOptions}
                            searchable
                            clearable
                            w={160}
                            disabled={isAssigning}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(val) => { if (val) handleAssignTask(task.id, val); }}
                            comboboxProps={{ zIndex: 1100, withinPortal: true }}
                          />
                        )}
                        {(isInProgress || isPending) && !isCompleted && !hasForm && (
                          <Button
                            size="compact-xs"
                            variant="light"
                            color="green"
                            loading={isCompleting}
                            onClick={(e) => { e.stopPropagation(); handleCompleteTask(task.id); }}
                          >
                            Complete
                          </Button>
                        )}
                      </Group>
                    </Group>
                    {task.dueAt && <Text size="xs" c="dimmed" mt={2}>Due: {formatTimestamp(task.dueAt)}</Text>}
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
